import { useEffect, useState } from "react";
import { BellRing, X, Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, enablePush } from "@/lib/push";

const SNOOZE_KEY = "pula_alerts_snooze_until";
const SNOOZE_MS = 2 * 24 * 60 * 60 * 1000; // re-invite after 2 days if they tap "Not now"

// Prominently invites signed-in members to turn on call & message alerts so friends can
// reach them even when Pula is closed — the key to WhatsApp-style ringing. It re-invites
// after a short snooze (rather than vanishing forever) to lift opt-in, without harassing.
export default function EnableAlertsPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !pushSupported()) return;
    if (pushPermission() !== "default") return; // already granted or blocked
    const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozeUntil && Date.now() < snoozeUntil) return;
    const t = setTimeout(() => setShow(true), 2500); // appear quickly after the page settles
    return () => clearTimeout(t);
  }, [user]);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Call & message alerts on — friends can now reach you even when Pula is closed. 🌧️");
      localStorage.removeItem(SNOOZE_KEY);
      setShow(false);
    } catch {
      toast.error("Couldn't turn on alerts. You can enable them anytime in Settings.");
    } finally { setBusy(false); }
  };

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-300" onClick={snooze} data-testid="enable-alerts-overlay" />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="enable-alerts-prompt"
        className="fixed z-[71] left-1/2 -translate-x-1/2 bottom-6 w-[calc(100%-2rem)] max-w-md pula-card p-6 shadow-2xl border-2 border-pula-green/30 animate-in slide-in-from-bottom-6 fade-in duration-400"
      >
        <button onClick={snooze} data-testid="enable-alerts-close" className="absolute top-3 right-3 text-pula-inksoft hover:text-pula-ink transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-4">
            <span className="absolute inset-0 rounded-full bg-pula-green/30 animate-ping" />
            <div className="relative w-16 h-16 rounded-full bg-pula-green grid place-items-center shadow-lg">
              <PhoneCall className="w-7 h-7 text-white" />
            </div>
          </div>

          <h3 className="font-heading font-extrabold text-pula-ink text-xl">Never miss a call again</h3>
          <p className="text-sm text-pula-inksoft font-body mt-2 leading-relaxed">
            Turn on Pula alerts so friends and family can <span className="font-semibold text-pula-ink">ring you even when the app is closed</span> — just like a normal phone call. You'll also get message alerts.
          </p>

          <button
            onClick={enable}
            disabled={busy}
            data-testid="enable-alerts-yes"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-pula-green text-white text-base font-bold shadow-md hover:brightness-105 active:scale-[0.98] transition disabled:opacity-70"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <BellRing className="w-5 h-5" />}
            {busy ? "Turning on…" : "Turn on call alerts"}
          </button>
          <button onClick={snooze} data-testid="enable-alerts-no" className="mt-2 text-sm text-pula-inksoft hover:text-pula-ink font-medium transition-colors">
            Maybe later
          </button>
          <p className="text-[11px] text-pula-inksoft/80 mt-3">You can change this anytime in Settings.</p>
        </div>
      </div>
    </>
  );
}
